import type {
  EdgeId,
  EdgeInstance,
  NodeInstance,
  PortId,
} from '../../../types/graph';
import { DeferredCompositeCommand } from '../composite';
import { CommandErrorKind } from '../types';
import { AddEdgeCommand, FailCommand, RemoveEdgeCommand } from '../elementary';
import { createAddNodeCommand } from '../elementary/add-node';
import { defaultIdFactory } from '../utils';

export interface InsertNodeOnEdgeCommandProps {
  edgeId: EdgeId;
  created: {
    // Id of input port of the created node to which we are connecting.
    inputPortId: PortId;
    // Id of output port of the created node to which we are connecting.
    outputPortId: PortId;
  };
  // Details of node to be created.
  node: Omit<NodeInstance, 'id'>;
}

export const createInsertNodeOnEdgeCommand = ({
  edgeId,
  created,
  node,
}: InsertNodeOnEdgeCommandProps) => {
  const newNodeId = defaultIdFactory.nodeId();
  const leftEdgeId = defaultIdFactory.edgeId();
  const rightEdgeId = defaultIdFactory.edgeId();

  return new DeferredCompositeCommand((graph) => {
    const oldEdge = graph.edges[edgeId];
    if (!oldEdge) {
      return [
        new FailCommand({
          kind: CommandErrorKind.NotFound,
          message: `Edge ${edgeId} not found`,
        }),
      ];
    }
    if (!created.inputPortId || !created.outputPortId) {
      return [
        new FailCommand({
          kind: CommandErrorKind.Validation,
          message: 'Inserted node must have both input and output ports',
        }),
      ];
    }

    const newNode: NodeInstance = {
      ...node,
      id: newNodeId,
    };
    const leftEdge: EdgeInstance = {
      id: leftEdgeId,
      order: oldEdge.order,
      from: {
        ...oldEdge.from,
      },
      to: {
        nodeId: newNodeId,
        portId: created.inputPortId,
      },
    };
    const rightEdge: EdgeInstance = {
      id: rightEdgeId,
      order: 0,
      from: {
        nodeId: newNodeId,
        portId: created.outputPortId,
      },
      to: {
        ...oldEdge.to,
      },
    };

    return [
      new RemoveEdgeCommand(edgeId),
      createAddNodeCommand(newNode),
      new AddEdgeCommand(leftEdge),
      new AddEdgeCommand(rightEdge),
    ];
  }, 'Insert node on edge');
};
